// U-04 fase a1 · lo que la pantalla de revisión calcula sin pintar nada.
//
// Vive aparte para que las pruebas lo midan sin montar la pantalla, y para
// que el texto de una regla (el motivo de un botón deshabilitado, la
// consecuencia de guardar) exista UNA vez.

export const ID_MIGRACION = 'migracion';

// Verde = alta, amarillo = media, rojo = baja. SIEMPRE con la palabra:
// un color solo no lo distingue quien no ve bien los colores.
export const CONFIANZA = {
  ALTA: { palabra: 'Alta', clase: 'bg-green-100 text-green-800 border-green-300' },
  MEDIA: { palabra: 'Media', clase: 'bg-yellow-100 text-yellow-900 border-yellow-300' },
  BAJA: { palabra: 'Baja', clase: 'bg-red-100 text-red-800 border-red-300' },
};

export const ESTADO = {
  PENDIENTE: 'Por decidir',
  APROBADA: 'Aprobada',
  RECHAZADA: 'Rechazada',
  APLICADA: 'Guardada',
};

/** Por qué «Aprobar las de confianza alta» no se puede pulsar, o ''. */
export function motivoAprobarAltas(n) {
  return n > 0 ? '' : 'No hay filas de confianza alta pendientes';
}

// Sin red los botones se APAGAN; no hay cola que guarde nada después (H8:
// el texto anterior prometía «se guardan al volver la señal», y no existe
// esa cola en a1). Lo ya decidido sí está en el servidor.
export const SIN_RED_AVISO = 'Sin conexión: mientras no vuelva la señal no se puede decidir ni guardar. '
  + 'Lo que ya decidiste sigue guardado.';
export const SIN_RED_MOTIVO = 'Sin conexión: espera a que vuelva la señal.';

/** Por qué «Guardar» no se puede pulsar, o ''. */
export function motivoGuardar(aprobadas, enLinea = true) {
  if (!enLinea) return SIN_RED_MOTIVO;
  return aprobadas > 0 ? '' : 'Aprueba al menos una fila para guardar';
}

// Filas por página de la revisión. El servidor acepta hasta 100.
export const POR_PAGINA = 50;

/** Las cifras de los botones y de la confirmación, del RESUMEN del
    servidor (toda la migración) y nunca de la página pintada (H2). */
export function cuentasDelServidor(resumen) {
  const g = resumen?.por_guardar || {};
  const porEstado = resumen?.por_estado || {};
  return {
    altas: Number(resumen?.aprobables_alta || 0),
    cuenta: { filas: Number(g.filas || 0), productos: Number(g.productos || 0),
              piezas: g.piezas ?? '0', ubicaciones: Number(g.ubicaciones || 0) },
    bloqueadas: Number(resumen?.bloqueadas || 0),
    enTotal: Object.values(porEstado).reduce((s, n) => s + Number(n || 0), 0),
  };
}

/** La consecuencia de guardar, en una frase (Doctrina 2, regla 7). */
export function consecuencia({ productos, piezas, ubicaciones }) {
  const alta = Number(productos) === 1 ? 'Se dará de alta 1 producto' : `Se darán de alta ${productos} productos`;
  const suma = Number(piezas) === 1 ? 'se sumará 1 pieza' : `se sumarán ${piezas} piezas`;
  const donde = Number(ubicaciones) === 1 ? '1 ubicación' : `${ubicaciones} ubicaciones`;
  return `${alta} y ${suma} en ${donde}. Lo guardado ya no se quita: se corrige contando.`;
}

/** Los pares de «Vienen dos veces»: filas con DUPLICADO sin decidir,
    agrupadas por la primera (la que nombra `duplicado_de`). */
/** «1 fila», «2 filas»: el número con su palabra en singular o plural. */
export function cuentaDeFilas(n, singular = 'fila', plural = 'filas') {
  return `${n} ${Number(n) === 1 ? singular : plural}`;
}

/** Los avisos tras subir, decidir y aprobar, con el número concordado (N5). */
export function avisoLeidas(n, archivo) {
  return n === 1
    ? `Se leyó 1 fila de ${archivo}. Revísala antes de guardar.`
    : `Se leyeron ${n} filas de ${archivo}. Revísalas antes de guardar.`;
}

export function avisoDecision({ cambiadas = 0, omitidas = [] } = {}) {
  if (omitidas.length) {
    return `${cuentaDeFilas(omitidas.length)} no se ${omitidas.length === 1 ? 'aprobó' : 'aprobaron'}: ${omitidas[0].motivo}`;
  }
  return cambiadas === 1 ? 'Listo: cambió 1 fila.' : `Listo: cambiaron ${cambiadas} filas.`;
}

export function avisoAltas(n) {
  return n === 1 ? 'Se aprobó 1 fila de confianza alta.' : `Se aprobaron ${n} filas de confianza alta.`;
}

/**
 * C3 de la segunda auditoría a1: cuántas filas repetidas faltan por decidir
 * y cuántas de ellas trajo la consulta. El servidor manda primero las que
 * faltan, así que al decidir éstas aparecen las demás.
 */
export function duplicadasPorDecidir(filasDuplicadas, resumen) {
  // C-b (tercera auditoría a1): se cuentan las filas que forman PAR. Una
  // repetida cuya pareja ya se guardó no sale en un par y se cuenta aparte.
  const vistas = paresDuplicados(filasDuplicadas || []).reduce((s, p) => s + p.length, 0);
  const total = Number(resumen?.duplicadas_sin_decidir ?? vistas);
  return { vistas, total, faltan: total > vistas };
}

/** C-b: el texto de las repetidas cuya pareja ya se guardó. */
export function avisoYaGuardadas(n) {
  if (!n) return '';
  return Number(n) === 1
    ? '1 fila repite un producto que ya se guardó en esta migración. Si la apruebas con «Sumar», sus piezas se suman a lo guardado; si no, recházala.'
    : `${n} filas repiten un producto que ya se guardó en esta migración. Si las apruebas con «Sumar», sus piezas se suman a lo guardado; si no, recházalas.`;
}

/** C-b: las repetidas sin decidir que la consulta trajo y no forman par. */
export function repetidasSinPar(filasDuplicadas) {
  const enPar = new Set(paresDuplicados(filasDuplicadas || []).flat().map((f) => f.id));
  return (filasDuplicadas || []).filter((f) => (f.estado === 'PENDIENTE' || f.estado === 'APROBADA')
    && !f.suma_confirmada && !enPar.has(f.id)
    && (f.senales || []).some((s) => s.codigo === 'DUPLICADO'));
}

export function paresDuplicados(filas) {
  const pares = new Map();
  for (const f of filas) {
    if (f.estado === 'RECHAZADA' || f.estado === 'APLICADA' || f.suma_confirmada) continue;
    if (!(f.senales || []).some((s) => s.codigo === 'DUPLICADO')) continue;
    const clave = f.duplicado_de ? f.duplicado_de.id : f.id;
    if (!pares.has(clave)) pares.set(clave, []);
    pares.get(clave).push(f);
  }
  return [...pares.values()].filter((p) => p.length > 1);
}

/** Lo que se haría con la fila, en palabras. */
export function queSeHaria(f) {
  const it = f.interpretacion || {};
  const producto = it.nombre_producto || it.nombre || it.sku || 'Producto sin nombre';
  if (f.efecto === 'PRODUCTO') return `Dar de alta «${producto}» (${it.sku})`;
  if (f.efecto === 'NINGUNO') return `«${producto}» ya existe: no se cambia`;
  const donde = it.ubicacion_nombre || it.ubicacion || 'sin ubicación';
  const nuevo = f.efecto === 'PRODUCTO_Y_EXISTENCIA' ? 'nuevo, ' : '';
  return `${it.cantidad ?? '?'} de «${producto}» (${nuevo}${it.sku || 'sin SKU'}) en ${donde}`;
}
